import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TextAreaFieldComponent } from '../../../../components/form/textarea-field/textarea-field.component';
import { AlertNotificationService } from '../../../../services/alert-notification.service';
import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';
import { AlertInfo } from 'src/app/models/alert-info';
import { FormService } from 'src/app/services/form.service';
import { ActivatedRoute } from '@angular/router';
import { HttpParams } from '@angular/common/http';
import { AuthService } from '../../../../services/api/auth.service';
import { ConfirmationDialogService } from '../../../../services/confirmation-dialog.service';


@Component({
  selector: 'notes-customers-form',
  imports: [CommonModule, TextAreaFieldComponent, ReactiveFormsModule, NgbAlert],
  templateUrl: './notes-customers-form.component.html',
  styleUrl: './notes-customers-form.component.scss'
})
export class FormNotesCustomersComponent implements OnInit, OnChanges {
  @Input() customer: any;
  @Input() readonlyCustomerNotes!: boolean;
  @Input() sectionAttribute: string = '';
  @Input() noteServiceMethod: any;
  @Input() addNoteMethod: string = '';
  @Input() deleteNoteMethod: string = '';
  @Input() listNoteMethod: string = '';
  @Input() customerId: any;
  @Input() secondaryId: any;
  customerId_internal: any;

  notesForm: FormGroup;
  previousNotes: any[] = [];
  alertInfo: AlertInfo = { show: false, type: '', message: '' };
  indexItemToBeDeleted: number = 0;
  deleteSectionNote: any;

  constructor(private fb: FormBuilder,
    private alertNotificationService: AlertNotificationService,
    private formService: FormService,
    private route: ActivatedRoute,
    private authService: AuthService,
    private confirmationDialogService: ConfirmationDialogService) {
    this.notesForm = this.fb.group({
      newNote: ['']
    });
  }

  ngOnInit() { 
    if (this.customerId) {
      this.customerId_internal = this.customerId;
    } else {
      this.customerId_internal = this.route.snapshot.paramMap.get(this.sectionAttribute);
    }
    
    setTimeout(() => {
      if (this.customer) {
        this.previousNotes = this.customer;        
      }
    }, 700);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['customer'] && changes['customer'].currentValue) {
      this.previousNotes = changes['customer'].currentValue;
    }
  }

  getNewNoteControl(): FormControl {
    return this.notesForm.get('newNote') as FormControl;
  }

  addNote() {    
    const noteContent = this.getNewNoteControl().value;
    this.authService.getUserInSession().subscribe(user => {
      if (noteContent && noteContent.trim() && user) {
        const newNote: any = {
          note: noteContent,
          creator: {
            firstName: user.firstName,
            lastName: user.lastName
          },
          createTime: new Date().toLocaleDateString('en-US', { 
            month: '2-digit', 
            day: '2-digit', 
            year: 'numeric' 
          })
        };

        if (this.customerId_internal) {
          const serviceToUse = this.noteServiceMethod;
          const methodToCall = this.addNoteMethod;

          if (serviceToUse && typeof serviceToUse[methodToCall] === 'function') {   
            const callPromise = this.secondaryId !== undefined && this.secondaryId !== null
              ? serviceToUse[methodToCall](Number(this.customerId_internal), Number(this.secondaryId), { note: noteContent })
              : serviceToUse[methodToCall](Number(this.customerId_internal), { note: noteContent });
            
            callPromise.subscribe({
              next: (response: any) => {
                this.submitFormSuccess(response);
                this.previousNotes.unshift(newNote);
                this.getNewNoteControl().reset();
                
                this.reloadNotes();
              },
              error: (error: any) => {
                this.submitFormError(error);
              }
            });
          } else {
            console.error(`Method ${methodToCall} not found in the provided service`);
          }
        }
      }
    });
  }

  confirmDelete(index: number, item: any, data: any) {
    this.indexItemToBeDeleted = index;
    this.deleteSectionNote = data.sectionNoteId;
    this.confirmationDialogService.open(
      `Are you confident in your decision to delete this ${item}?`,
      () => this.deleteNote(index)
    );
  }

  deleteNote(index: number) {
    if (this.customerId_internal && this.deleteSectionNote) {
      const serviceToUse = this.noteServiceMethod;
      const methodToCall = this.deleteNoteMethod;

      if (serviceToUse && typeof serviceToUse[methodToCall] === 'function') {   
        const callPromise = this.secondaryId !== undefined && this.secondaryId !== null
          ? serviceToUse[methodToCall](Number(this.customerId_internal), Number(this.secondaryId), this.deleteSectionNote)
          : serviceToUse[methodToCall](Number(this.customerId_internal), this.deleteSectionNote);
        
        callPromise.subscribe({
          next: () => {
            this.previousNotes.splice(index, 1);
            this.alertNotificationService.success('Note successfully removed');
            this.reloadNotes();
          },
          error: (error: any) => {
            this.alertNotificationService.error('Failed to delete note');
            console.error('Error deleting note:', error);
          },
        });
      } else {
        console.error(`Method ${methodToCall} not found in the provided service`);
      }
    }
  }

  normalizeNoteText(note: string | null | undefined): string {
    if (!note) {
      return '';
    }
    return note.replace(/\r\n/g, '\n');
  }

  formatPreviousNotes(): string {
    return this.previousNotes
      .map(note => `${note.author} ${note.timestamp}\n${note.content}`)
      .join('\n\n');
  }

  submitForm(): void {
    this.alertNotificationService.success('Operation completed successfully')
    return this.notesForm.value
  }


  /**
    * *******************
    * Submit form methods
    * *******************
    */
  showAlertInfo(type: string, message: string) {
    this.alertInfo = {
      show: true,
      type,
      message
    }
    setTimeout(() => {
      this.alertInfo.show = false;
    }, 3000);
  }
  updateValidationErrors(validationErrors: any) {
    this.formService.resetErrors(this.notesForm);
    this.formService.setValidationErrors(this.notesForm, validationErrors);
  }
  submitFormSuccess(response: any) {
    this.alertNotificationService.success('Operation completed successfully')
    this.showAlertInfo('success', 'Operation completed successfully');
    setTimeout(() => {
      this.alertInfo.show = false
    }, 3000);
  }
  submitFormError(error: any) {

    if (error.error.error.description == 'ARGUMENT_VALIDATION_ERROR') {
      this.updateValidationErrors(error.error.error.validationErrors);
    }
    else if (error.error.error.description == 'USER_EMAIL_ALREADY_EXISTS') {
      this.updateValidationErrors([{ field: 'email', defaultMessage: error.error.error.message }]);
      this.showAlertInfo('danger', `Please fix the error: ${error.error.error.message}`);
    }
    else {
      this.showAlertInfo('danger', error.error.error.message);
    }
    setTimeout(() => {
      this.alertInfo.show = false
    }, 3000);
  }

  isFormValid(): boolean {
    return this.notesForm.valid;
  }

 
  reloadNotes(): void {
    if (this.customerId_internal && this.noteServiceMethod) {
      const params = new HttpParams().set('size', '9999');
      

      let listNotesMethod = 'listNotes';
      if (this.listNoteMethod) {
        listNotesMethod = this.listNoteMethod;
      } else if (this.addNoteMethod && this.addNoteMethod.includes('ProjectIssues')) {
        listNotesMethod = 'listProjectIssuesNotes';
      }
      
      if (this.noteServiceMethod && typeof this.noteServiceMethod[listNotesMethod] === 'function') {

        const callPromise = this.secondaryId !== undefined && this.secondaryId !== null
          ? this.noteServiceMethod[listNotesMethod](Number(this.customerId_internal), Number(this.secondaryId), { params })
          : this.noteServiceMethod[listNotesMethod](Number(this.customerId_internal), { params });
        
        callPromise.subscribe({
          next: (response: any) => {
            if (response.status && response.status.code === 200) {
              this.previousNotes = response?.data?.sectionNotes || [];
            }
          },
          error: (error: any) => {
            console.error('Error reloading notes:', error);
          }
        });
      } else {
        console.error(`Method ${listNotesMethod} not found in the provided service`);
      }
    }
  }
}
